import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  ZoomControl,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import {
  fetchMapDispensaries,
  fetchFacets,
  fetchDispensariesByIds,
  createSnapshot,
  fetchBusTastings,
} from "../lib/api";
import { toast } from "sonner";
import { useDepot } from "../lib/useDepot";
import { buildGoogleMapsRouteUrl } from "../lib/routes";
import {
  MAX_ROUTE_STOPS,
  displayName,
  clusterIconCreate,
  FitToMarkers,
  MapRefCapture,
} from "../components/map/mapHelpers";
import { MarkerDot } from "../components/map/MarkerDot";
import { Legend } from "../components/map/Legend";
import { FilterPanel } from "../components/map/FilterPanel";
import { RoutePanel } from "../components/map/RoutePanel";

// California bounding box default view
const CA_CENTER = [36.7783, -119.4179];
const CA_ZOOM = 6;

export default function MapPage() {
  const [items, setItems] = useState(null);
  const [facets, setFacets] = useState({ counties: [], cities: [], pairs: [] });
  const [confidence, setConfidence] = useState("ALL");
  const [county, setCounty] = useState("ALL");
  const [city, setCity] = useState("ALL");
  const [mapReady, setMapReady] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [bookendDepot, setBookendDepot] = useState(() => {
    // Persist preference per device; bus driver opts in once.
    try {
      return localStorage.getItem("jacktrack_bookend_depot") === "1";
    } catch {
      return false;
    }
  });
  const depot = useDepot();
  const mapRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    try {
      localStorage.setItem("jacktrack_bookend_depot", bookendDepot ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [bookendDepot]);

  useEffect(() => {
    // Defer MapContainer mount one tick so StrictMode's double-render doesn't
    // double-initialize leaflet on the same DOM node.
    const t = setTimeout(() => setMapReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    fetchFacets().then(setFacets);
  }, []);

  // Pre-select stops from `?ids=...`, `?focus=<id>`, or `?tasting_date=YYYY-MM-DD`.
  // - ids:           multi-stop route from a lead list
  // - focus:         single dispensary (e.g. "View on map" from Bus Tastings)
  // - tasting_date:  ALL dispensaries with a scheduled tasting on that date,
  //                  auto-selected for one-click route planning
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idsParam = params.get("ids");
    const focusId = params.get("focus");
    const tastingDate = params.get("tasting_date");

    if (!idsParam && !focusId && !tastingDate) return;

    let cancelled = false;
    (async () => {
      try {
        let ids = [];
        let bannerLabel = null;
        if (idsParam) {
          ids = idsParam.split(",").map((s) => s.trim()).filter(Boolean);
        } else if (focusId) {
          ids = [focusId];
        } else if (tastingDate) {
          // Look up all scheduled tastings on this date.
          const tastings = await fetchBusTastings({ scheduled_date: tastingDate, status: "scheduled" });
          ids = tastings.map((t) => t.dispensary_id);
          bannerLabel = `Planning route for ${tastingDate} · ${tastings.length} bus event${tastings.length === 1 ? "" : "s"}`;
        }
        if (!ids.length) return;
        // Fetch only the requested ids (cheap, ordered).
        const docs = await fetchDispensariesByIds(ids);
        if (cancelled) return;
        const stops = docs
          .filter((d) => typeof d.latitude === "number" && typeof d.longitude === "number")
          .map((d) => ({
            id: d.id,
            name: displayName(d),
            city: d.city,
            latitude: d.latitude,
            longitude: d.longitude,
          }));
        if (stops.length) {
          setSelected(stops.slice(0, MAX_ROUTE_STOPS));
          // Pan to the first stop after the map is ready.
          setTimeout(() => {
            try {
              mapRef.current?.flyTo([stops[0].latitude, stops[0].longitude], 10, {
                duration: 0.8,
              });
            } catch {
              /* map not ready */
            }
          }, 800);
          if (stops.length > MAX_ROUTE_STOPS) {
            toast.info(
              `Loaded first ${MAX_ROUTE_STOPS} stops · the full list had ${ids.length}.`,
              { description: "Remove a stop to swap in the next one." }
            );
          } else if (bannerLabel) {
            toast.success(bannerLabel);
          } else if (focusId) {
            toast.success(`Focused on ${stops[0]?.name || "dispensary"}`);
          } else {
            toast.success(`Pre-loaded ${stops.length} stops from your lead list`);
          }
        }
      } catch (e) {
        toast.error("Could not pre-load route", { description: String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const toggleSelect = (d) => {
    setSelected((prev) => {
      const isSel = prev.some((x) => x.id === d.id);
      if (isSel) return prev.filter((x) => x.id !== d.id);
      if (prev.length >= MAX_ROUTE_STOPS) {
        toast.error(`Max ${MAX_ROUTE_STOPS} stops per route`, {
          description: "Google Maps' free URL format caps routes at 9 destinations + origin.",
        });
        return prev;
      }
      return [
        ...prev,
        {
          id: d.id,
          name: displayName(d),
          city: d.city,
          latitude: d.latitude,
          longitude: d.longitude,
        },
      ];
    });
  };

  const clearSelection = () => setSelected([]);

  const planRoute = () => {
    if (selected.length < 1) {
      toast.error("Pick at least 1 stop to plan a route");
      return;
    }
    const useDepotBookend = bookendDepot && depot;
    if (!useDepotBookend && selected.length < 2) {
      toast.error("Pick at least 2 stops, or enable Start & end at depot");
      return;
    }
    const url = buildGoogleMapsRouteUrl(selected, {
      depot: useDepotBookend ? depot : null,
    });
    if (!url) {
      toast.error("Could not build route URL");
      return;
    }
    window.open(url, "_blank");
    toast.success("Opening route in Google Maps", {
      description: useDepotBookend
        ? `Depot → ${selected.length} stops → Depot`
        : `${selected.length} stops · turn-by-turn ready`,
    });
  };

  const saveAsLeadList = async () => {
    if (selected.length < 1) {
      toast.error("Pick at least 1 stop to save");
      return;
    }
    const title = window.prompt(
      "Name this lead list / route",
      `Route · ${selected.length} stops · ${new Date()
        .toISOString()
        .slice(0, 10)}`
    );
    if (!title) return;
    try {
      setSavingList(true);
      const snap = await createSnapshot({
        title: title.trim(),
        subtitle: `Map-built route · ${selected.length} hand-picked stops`,
        filters: {
          dispensary_ids: selected.map((s) => s.id),
          limit: selected.length,
          sort: "name",
          include_closed: false,
        },
      });
      toast.success("Lead list saved", {
        description: `Open from Lead Generator · ${snap.total_count} stops`,
        action: {
          label: "Open",
          onClick: () => window.open(`/snapshots/${snap.id}`, "_blank"),
        },
      });
    } catch (e) {
      toast.error("Could not save lead list", { description: String(e) });
    } finally {
      setSavingList(false);
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = searchQ.trim();
    if (!q) return;
    setSearching(true);
    try {
      const hasCA = /\b(ca|california)\b/i.test(q);
      const url =
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          hasCA ? q : `${q}, California`
        )}` +
        `&format=json&limit=1&countrycodes=us`;
      const resp = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      const data = await resp.json();
      if (!Array.isArray(data) || data.length === 0) {
        toast.error("No results for that address", {
          description: "Try a city name, ZIP, or street + city.",
        });
        return;
      }
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);
      if (mapRef.current) {
        mapRef.current.flyTo([lat, lng], 13, { duration: 1.2 });
      }
    } catch (err) {
      toast.error("Search failed", { description: err?.message });
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const params = {
      confidence: confidence !== "ALL" ? confidence : undefined,
      county: county !== "ALL" ? county : undefined,
      city: city !== "ALL" ? city : undefined,
      limit: 5000,
    };
    setItems(null);
    fetchMapDispensaries(params).then(setItems);
  }, [confidence, county, city]);

  // Build city↔county lookup from facets pairs (same pattern as
  // Dispensaries / Lead Generator). Powers the linked filter behaviour
  // — pick a city → county auto-fills; pick a county → city dropdown
  // narrows to that county's cities only.
  const { cityToCounty, countyToCities } = useMemo(() => {
    const cToCntyCounts = {};
    const cntyToCSet = {};
    (facets.pairs || []).forEach(({ city: c, county: cn, count }) => {
      if (c && cn) {
        cToCntyCounts[c] = cToCntyCounts[c] || {};
        cToCntyCounts[c][cn] = (cToCntyCounts[c][cn] || 0) + (count || 1);
        cntyToCSet[cn] = cntyToCSet[cn] || new Set();
        cntyToCSet[cn].add(c);
      }
    });
    const cityToCounty = {};
    Object.entries(cToCntyCounts).forEach(([c, counts]) => {
      cityToCounty[c] = Object.entries(counts).sort(
        (a, b) => b[1] - a[1]
      )[0][0];
    });
    const countyToCities = {};
    Object.entries(cntyToCSet).forEach(([cn, set]) => {
      countyToCities[cn] = Array.from(set).sort();
    });
    return { cityToCounty, countyToCities };
  }, [facets.pairs]);

  const cityOptions = useMemo(() => {
    if (county !== "ALL" && countyToCities[county]) {
      return countyToCities[county];
    }
    return facets.cities || [];
  }, [county, countyToCities, facets.cities]);

  const handleCityChange = (next) => {
    const v = next || "ALL";
    setCity(v);
    if (v !== "ALL") {
      const matched = cityToCounty[v];
      if (matched && county !== matched) setCounty(matched);
    }
  };
  const handleCountyChange = (next) => {
    const v = next || "ALL";
    setCounty(v);
    if (v !== "ALL" && city !== "ALL" && cityToCounty[city] !== v) {
      setCity("ALL");
    }
  };

  const points = useMemo(
    () =>
      (items || []).filter(
        (d) =>
          typeof d.latitude === "number" &&
          typeof d.longitude === "number"
      ),
    [items]
  );

  return (
    <div
      className="relative -mx-8 -my-6"
      style={{ height: "calc(100vh - 73px)" }}
    >
      <FilterPanel
        searchQ={searchQ}
        setSearchQ={setSearchQ}
        searching={searching}
        runSearch={runSearch}
        county={county}
        handleCountyChange={handleCountyChange}
        facetsCounties={facets.counties}
        city={city}
        handleCityChange={handleCityChange}
        cityOptions={cityOptions}
        confidence={confidence}
        setConfidence={setConfidence}
      />

      <RoutePanel
        selected={selected}
        clearSelection={clearSelection}
        removeStop={toggleSelect}
        planRoute={planRoute}
        saveAsLeadList={saveAsLeadList}
        savingList={savingList}
        bookendDepot={bookendDepot}
        setBookendDepot={setBookendDepot}
        depot={depot}
      />

      {items === null && (
        <div
          data-testid="map-loading"
          className="absolute top-4 right-4 z-[400] panel px-3 py-2 bg-zinc-950/85 backdrop-blur-md border-zinc-800 text-xs text-zinc-300 inline-flex items-center gap-2"
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
          Loading…
        </div>
      )}

      {items !== null && items.length === 0 && (
        <div
          data-testid="map-empty"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[400] panel p-6 bg-zinc-950/90 backdrop-blur-md border-zinc-800 text-center max-w-md"
        >
          <div className="text-sm font-medium text-zinc-100">
            No dispensaries match these filters
          </div>
          <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
            Click <strong className="text-emerald-400">Run Scan</strong> on the
            Dashboard to pull the latest California DCC license list — every
            storefront retailer has GPS coordinates and will appear here.
          </div>
        </div>
      )}

      {mapReady && (
        <div
          data-testid="map-container"
          style={{ height: "100%", width: "100%" }}
        >
          <MapContainer
            center={CA_CENTER}
            zoom={CA_ZOOM}
            scrollWheelZoom
            zoomControl={false}
            style={{ height: "100%", width: "100%", background: "#9a978f" }}
          >
            <MapRefCapture onMap={(m) => (mapRef.current = m)} />
            <ZoomControl position="topright" />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              subdomains={["a", "b", "c"]}
              maxZoom={19}
            />
            <FitToMarkers points={points} />
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={clusterIconCreate}
              showCoverageOnHover={false}
              /* Smaller pixel radius + forced unclustering at metro zoom so
                 clusters explode into individual dots sooner — easier for the
                 route-planner to eyeball where shops actually sit. */
              maxClusterRadius={45}
              disableClusteringAtZoom={11}
              spiderfyOnMaxZoom={true}
              removeOutsideVisibleBounds={true}
            >
              {points.map((d) => (
                <MarkerDot
                  key={d.id}
                  d={d}
                  isSelected={selected.some((s) => s.id === d.id)}
                  onToggle={toggleSelect}
                />
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      )}

      <Legend count={points.length} />
    </div>
  );
}
